// Core packages
import {
  Component, ElementRef, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { formatDate, KeyValue } from "@angular/common";
import { MatDialog } from "@angular/material";
import { Sort } from "@angular/material/sort";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { ExportContractsDialogComponent } from 'src/app/modules/properties/components/export-contracts-dialog/export-contracts-dialog.component';
import { ExportRasDocsDialogComponent } from 'src/app/modules/properties/components/export-ras-docs-dialog/export-ras-docs-dialog.component';
import { ConductorManagementDialogComponent } from 'src/app/modules/properties/components/conductor-management-dialog/conductor-management-dialog.component';
import { InterventionArchivingDialogComponent } from 'src/app/modules/properties/components/intervention-archiving-dialog/intervention-archiving-dialog.component';
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { SuppliersService } from "src/app/modules/suppliers/suppliers.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ViewStructureDialogComponent } from "src/app/shared/components/dialogs/view-structure-dialog/view-structure-dialog.component";
import { Company } from "src/app/shared/models/company/company.model";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import {
  DateDocumentFormatPipe, DateTimeFormatPipe
} from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { CancelContractDialogComponent } from "../../components/cancel-contract-dialog/cancel-contract-dialog.component";
import { PropertyExpireContractItemDialogComponent } from "../../components/contract-expire-item-dialog/contract-expire-item-dialog.component";
import { PropertyDeleteDocumentItemDialogComponent } from "../../components/document-delete-item-dialog/document-delete-item-dialog.component";
import { DocumentDownloadDialogComponent } from "../../components/document-download-dialog/document-download-dialog.component";
import { PropertyDetailDocumentInsertDialogComponent } from "../../components/document-insert-dialog/document-insert-dialog.component";
import { PropertyDetailDocumentItemDialogComponent } from "../../components/document-item-dialog/document-item-dialog.component";
import { PropertyDetailDocumentItemPrintDialogComponent } from "../../components/document-item-print-dialog/document-item-print-dialog.component";
import { PropertyDetailDocumentNoteDialogComponent } from "../../components/document-note-dialog/document-note-dialog.component";
import { DocumentPaperInsertDialogComponent } from "../../components/document-paper-insert-dialog/document-paper-insert-dialog.component";
import { PropertyDetailDocumentPrintModuleDialogComponent } from "../../components/document-print-module-dialog/document-print-module-dialog.component";
import {
  PropertySameCodeEditDialogComponent,
  SameCodeEditDialogData,
  SameCodeEditDialogResult,
} from "../../components/document-same-code-edit-dialog/document-same-code-edit-dialog.component";
import { SendEmailDialogComponent } from "../../components/send-email-dialog/send-email-dialog.component";
import { CondoStructuresService } from "../../condo-structures.service";
import { DocumentArchivesService } from "../../document-archives.service";
import { AllDocsInterface, DocumentInterface } from "../../documents.interface";
import DocumentTypeStructure from "../../documentTypes";
import {
  buildSameCodeChoices,
  buildSameCodeEditTargets,
  collectSameCodeFormFields,
  SameCodeEditTarget,
} from "../../same-code-docs-edit.utils";
import {
  applySanitaryWaterMeterNames,
  getSanitaryWaterMeterNames,
} from "../../sanitary-water-meters.utils";

@Component({
  selector: "app-property-documents",
  templateUrl: "./property-documents.page.html",
  styleUrls: ["./property-documents.page.scss"],
})
export class PropertyDocumentsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  loggedUserObj: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  loggedUserRoleType = "";
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = "";
  loggedUserShared = false;
  loggedUserTemporary = false;
  loggedUserMaster = false;
  loggedUserAdmin = false;
  dateTimeFormat: DateTimeFormatPipe;
  dateDocumentFormat: DateDocumentFormatPipe;
  activeSection: string; // Active document section
  activeSectionData: any[] = [];
  itemId: string; // Id of active mapping section item
  condoStructure: any;
  documentArchive: any;
  documentArchiveId: string;
  allDocs: AllDocsInterface[] = [];
  displayedDocs: AllDocsInterface[] = [];
  documentsStructure: DocumentInterface[] = [];
  sortedDocuments: any[] = [];
  public documentTypeStructure: DocumentTypeStructure =
    new DocumentTypeStructure();
  documentArchiveIsLoading = true;
  // "Modifica dati aggiuntivi": lookup of the rows sharing the code still running
  sameCodeLookupInProgress = false;
  showDocumentsExpired = true;
  showDocumentsNotInserted = true;
  showStructureDeleted = false;
  showDocumentsNotVirtual = true;
  // insertDocuments = false;
  // insertDocumentsSelected = false;
  // insertDocumentsSelectedKey: any[] = [];
  company: Company = new Company();
  pageIsLoading = true;
  insertedDocs: any[] = [];
  selectedDocs: any[] = [];

  editDocument = true;

  totMandatoryDocs = 0;
  totInsertDocs = 0;
  totNotInsertDocs = 0;
  totOptionalDocs = 0;
  totDocs = 0;
  progressBarVal = 0;
  progressBarValStyle = { width: "0px" };
  subscriptionType = "FULL";
  structureSelected = [];
  showSideMenu = true;

  @ViewChild("scrollTarget", { static: false, read: ElementRef })
  public target: ElementRef<any>;

  currentOriginalOrder: any;
  sortActive = false;

  companyData = {};
  propertyName = "";

  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  documentTemplateId = "";
  documentTemplate: any[] = [];

  notesExist = false;

  condoStructureTemplate: any[] = [];

  masterAndSubscriptionPrivacy = false;

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,
    public newMessageDialog: MatDialog,
    public cancelContractDialog: MatDialog,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    private cookieService: CookieService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private condoStructuresService: CondoStructuresService,
    private rolesService: RolesService,
    public helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private companiesService: CompaniesService,
    private suppliersService: SuppliersService,
    private settingsService: SettingsService,
  ) {
    // this.documentsStructure = DocumentsStructure;
  }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.documentsPage.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        const currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        this.loggedUserRoleType = currentRole.role.type;
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleObj$.subscribe(
        (res) => (this.loggedUserObj = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role;
    if (this.currentRole.role.type === "shared") {
      this.loggedUserShared = true;

      this.showDocumentsExpired = false;
      this.showDocumentsNotInserted = false;
      this.showDocumentsNotVirtual = true;
    }
    if (this.currentRole.role.type === "temporary") {
      this.loggedUserTemporary = true;

      this.showDocumentsExpired = false;
      
      this.showDocumentsNotInserted = true;
      if (
        typeof this.currentRole.propertiesPermission.docsInserted !== "undefined"
        && this.currentRole.propertiesPermission.docsInserted
      ) {
        this.showDocumentsNotInserted = false;
      }
      
      this.showDocumentsNotVirtual = true;
    }
    if (
      this.currentRole.role.type === "master" ||
      this.currentRole.role.type === "collaborator"
    ) {
      this.loggedUserMaster = true;
      if (this.currentRole.company._id !== "") {
        this.subscriptions.push(
          this.companiesService
            .get(this.currentRole.company._id)
            .subscribe((res) => {
              if (res.status) {
                this.companyData = res.data;
              }
            })
        );
      }
    }
    this.loggedUserAdmin =
      this.loggedUserRole.type === "admin" ||
      this.loggedUserRole.type === "operator";

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.subscriptions.push(
      this.settingsService.getStructure().subscribe(
        (res) => {
          if (res.status) {
            if (
              res.condoStructureTemplate &&
              Array.isArray(res.condoStructureTemplate) &&
              res.condoStructureTemplate.length > 0 &&
              res.condoStructureTemplate[0].area &&
              Array.isArray(res.condoStructureTemplate[0].area)
            ) {
              this.condoStructureTemplate = res.condoStructureTemplate[0].area;
            }
          }
        }
      )
    );

    // Get requested property data, company data and document archive
    this.propertyId = this.activatedRoute.snapshot.params.propertyId;
    this.subscriptions.push(
      this.propertiesService.get(this.propertyId).subscribe(
        (res) => {
          if (res.status) {
            this.property.setData(res.data);
            this.propertyData = res.data;
            this.propertyName = res.data.name;

            if (
              this.propertyData &&
              this.propertyData.subscription &&
              this.propertyData.subscription.key
            ) {
              this.subscriptionType = this.propertyData.subscription.key;

              if (this.subscriptionType === 'PRIVACY' && this.loggedUserMaster) {
                this.masterAndSubscriptionPrivacy = true;
              }
            }

            if (
              this.propertyData.admin &&
              this.propertyData.admin.company &&
              this.propertyData.admin.company._id
            ) {
              this.subscriptions.push(
                this.companiesService
                  .get(this.propertyData.admin.company._id)
                  .subscribe((res) => {
                    if (res.status) {
                      this.company.setData(res.data);
                    }
                  })
              );
            }

            // If property has a structure ID, than retrive related data
            // and document archive
            if (
              this.subscriptionType !== "FREE" &&
              this.subscriptionType !== "FREEBASIC" &&
              this.propertyData.structure
            ) {
              // // Hide all visible elements of side menu
              // for (const key in this.documentsStructure) {
              //   this.documentsStructure[key].visible = false;
              //   if (Array.isArray(this.documentsStructure[key].content)) {
              //     this.documentsStructure[key].content.forEach((el: any) => {
              //       el.visible = false;
              //     });
              //   }
              // }
              this.subscriptions.push(
                this.settingsService.getDocument().subscribe(
                  (res) => {
                    if (res.status) {
                      if (
                        res.data &&
                        Array.isArray(res.data) &&
                        res.data.length > 0 &&
                        res.data[0].area &&
                        Array.isArray(res.data[0].area)
                      ) {
                        this.documentTemplateId = res.data[0]._id;
                        this.documentTemplate = res.data[0].area;

                        this.documentsStructure = this.documentTemplate.map((elDT: any) => {
                          let subEl = [];
                          if (Array.isArray(elDT.content)) {
                            subEl = elDT.content.map((subElDT: any) => {
                              return {
                                title: subElDT.title,
                                translation: subElDT.translation,
                                type: subElDT.type,
                                expanded: subElDT.expanded,
                                section: subElDT.section,
                                visible: subElDT.visible,
                                key: subElDT.key,
                              };
                            });
                          }
                          return {
                            title: elDT.title,
                            translation: elDT.translation,
                            type: elDT.type,
                            section: "",
                            buttonTitle: "",
                            expanded: elDT.expanded,
                            icon: "",
                            visible: elDT.visible,
                            content: subEl,
                          }
                        })
                        // Hide all visible elements of side menu
                        for (const key in this.documentsStructure) {
                          this.documentsStructure[key].visible = false;
                          if (Array.isArray(this.documentsStructure[key].content)) {
                            this.documentsStructure[key].content.forEach((el: any) => {
                              el.visible = false;
                            });
                          }
                        }

                        this.getStructureData();
                      }
                    }
                  },
                  (err) => {
                    //
                  }
                )
              );

              // this.getStructureData();

            } else if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
              this.subscriptions.push(
                this.settingsService.getDocument().subscribe(
                  (res) => {
                    if (res.status) {
                      if (
                        res.data &&
                        Array.isArray(res.data) &&
                        res.data.length > 0 &&
                        res.data[0].area &&
                        Array.isArray(res.data[0].area)
                      ) {
                        this.documentTemplateId = res.data[0]._id;
                        this.documentTemplate = res.data[0].area;

                        this.documentsStructure = this.documentTemplate.map((elDT: any) => {
                          let subEl = [];
                          if (Array.isArray(elDT.content)) {
                            subEl = elDT.content.map((subElDT: any) => {
                              return {
                                title: subElDT.title,
                                translation: subElDT.translation,
                                type: subElDT.type,
                                expanded: subElDT.expanded,
                                section: subElDT.section,
                                visible: subElDT.visible,
                                key: subElDT.key,
                              };
                            });
                          }
                          return {
                            title: elDT.title,
                            translation: elDT.translation,
                            type: elDT.type,
                            section: "",
                            buttonTitle: "",
                            expanded: elDT.expanded,
                            icon: "",
                            visible: elDT.visible,
                            content: subEl,
                          }
                        });
                        // Show all elements of side menu
                        for (const key in this.documentsStructure) {
                          this.documentsStructure[key].visible = true;
                          if (Array.isArray(this.documentsStructure[key].content)) {
                            this.documentsStructure[key].content.forEach((el: any) => {
                              el.visible = true;
                            });
                          }
                        }

                        // If property has a document Archive ID, than retrive related data
                        if (this.propertyData.documentArchive) {
                          this.documentArchiveId = this.propertyData.documentArchive;
                          this.getDocumentArchiveDataFree();
                          this.pageIsLoading = false;
                        }
                      }
                    }
                  },
                  (err) => {
                    //
                  }
                )
              );
              
            } else {
              this.documentArchiveIsLoading = false;
            }
            this.pageIsLoading = false;
            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "properties", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Get courrent property structure data and populate
   * this.condoStructure.
   * Then get archive document.
   *
   * @since 1.0.0
   */
  getStructureData(): void {
    this.condoStructuresService
      .get(this.property.get("structure"))
      .subscribe((res) => {
        if (res.status) {
          this.condoStructure = res.data;
          if (this.condoStructure.open) {
            this.editDocument = false;
          }

          for (const key in this.condoStructure) {
            if (
              Array.isArray(this.condoStructure[key]) &&
              this.condoStructure[key].length
            ) {
              this.condoStructure[key].every((el: any) => {
                if (el.note && Array.isArray(el.note) && el.note.length) {
                  this.notesExist = true;
                  return false;
                }
              });
              if (this.notesExist) {
                break;
              }
            }
          }

          // If property has a document Archive ID, than retrive related data
          if (this.propertyData.documentArchive) {
            this.documentArchiveId = this.propertyData.documentArchive;
            this.getDocumentArchiveData();
            this.pageIsLoading = false;
          }
        } else {
          this.documentArchiveIsLoading = false;
          this.toastrService.error(res.message);
        }
      });
  }

  /**
   * Get current property documentArchive data and populate
   * this.documentArchive
   * this.allDocs is used to show all docs in page
   *
   * @since 1.0.0
   */
  getDocumentArchiveData(): void {
    this.documentArchiveIsLoading = true;
    this.insertedDocs = [];
    this.documentArchivesService
      .get(this.property.get("documentArchive"))
      .subscribe((res) => {
        if (res.status) {
          this.documentArchive = res.data;
          if (this.documentArchive.suppliersName) {
            this.suppliersName = this.documentArchive.suppliersName;
          }
          if (this.documentArchive.yearsOfStorage) {
            this.yearsOfStorage = this.documentArchive.yearsOfStorage;
          }
          if (this.documentArchive.conductorName) {
            this.conductorName = this.documentArchive.conductorName;
          }
          if (this.documentArchive.nameInterventionArchiving) {
            this.nameInterventionArchiving =
              this.documentArchive.nameInterventionArchiving;
          }

          this.allDocs = this.documentTypeStructure.getDocumentArchiveData(
            this.lang,
            this.documentTemplate,
            this.condoStructure,
            this.documentArchive,
            this.subscriptionType,
            this.insertedDocs,
            true
          );
          // The last argument above keeps the soft deleted documents of deleted structures
          // in the tree. Only the rows already queued for the hard delete cron are dropped
          // here: that queue marks a manual deletion, so a document deleted from this page
          // does not come back after the reload, while the rows soft deleted by the cascade
          // of a removed mapping element stay and keep their deleted structure visible.
          this.removeHardDeletedDocs(this.allDocs);
          // ND1, ND2, ND25 and ND26 belong to the property and carry no meter: their rows
          // get the names of the sanitary water meters that make them due, printed in
          // parentheses in their section and in the Contratti table.
          applySanitaryWaterMeterNames(
            this.allDocs,
            getSanitaryWaterMeterNames(this.condoStructure)
          );

          // Only Privacy
          if (
            this.subscriptionType === 'PRIVACY' &&
            !this.loggedUserAdmin
          ) {
            this.allDocs = this.allDocs.filter(
              (el: AllDocsInterface) => el.keyTranslateTitle === "properties.documents.gestionaleAmministrativo"
            );
            this.allDocs.forEach((el: any) => {
              if (Array.isArray(el.content)) {
                el.content = el.content.filter(
                  (subEl: any) => 
                    subEl.keyTranslateTitle === "properties.documents.Aprivacy" || 
                    subEl.keyTranslateTitle === "properties.documents.gestAmmTivoDipendenti" ||
                    subEl.keyTranslateTitle === "properties.documents.gestAmmTivoGestionali"
                );
              }
            });
            this.allDocs.forEach((el: any) => {
              if (Array.isArray(el.content)) {
                el.content.forEach((subel: any) => {
                  if (subel.type === "doc" && Array.isArray(subel.content)) {
                    subel.content = subel.content.filter(
                      (subEl: any) => 
                        subEl.privacy
                    );
                  } else if (subel.type === "title" && Array.isArray(subel.content)) {
                    subel.content.forEach((subsubel: any) => {
                      if (Array.isArray(subsubel.content)) {
                        subsubel.content = subsubel.content.filter(
                          (subsubEl: any) => 
                            subsubEl.privacy
                        );
                      }
                    });
                  }
                });
              }
            });
          }

          let filteredData = [];
          if (this.allDocs && Array.isArray(this.allDocs)) {
            filteredData = this.filterDisplayedDocs(this.allDocs);
          }
          this.displayedDocs = filteredData;

          this.countDocPerArea();
        } else {
          this.toastrService.error(res.message);
        }

        this.documentArchiveIsLoading = false;
      });
  }

  /**
   * Only for FREE subscription
   * Get current property documentArchive data and populate
   * this.documentArchive
   * this.allDocs is used to show all docs in page
   *
   * @since 1.0.0
   */
  getDocumentArchiveDataFree(): void {
    this.documentArchiveIsLoading = true;
    this.insertedDocs = [];
    this.documentArchivesService
      .get(this.property.get("documentArchive"))
      .subscribe((res) => {
        if (res.status) {
          this.documentArchive = res.data;
          if (this.documentArchive.suppliersName) {
            this.suppliersName = this.documentArchive.suppliersName;
          }
          if (this.documentArchive.yearsOfStorage) {
            this.yearsOfStorage = this.documentArchive.yearsOfStorage;
          }
          if (this.documentArchive.conductorName) {
            this.conductorName = this.documentArchive.conductorName;
          }
          if (this.documentArchive.nameInterventionArchiving) {
            this.nameInterventionArchiving =
              this.documentArchive.nameInterventionArchiving;
          }

          
          this.allDocs = this.documentTypeStructure.getDocumentArchiveDataFree(
            this.lang, 
            this.documentTemplate, 
            this.documentArchive,
            this.subscriptionType,
            this.insertedDocs,
          );
          let filteredData = [];
          if (this.allDocs && Array.isArray(this.allDocs)) {
            filteredData = this.filterDisplayedDocs(this.allDocs);
          }
          this.displayedDocs = filteredData;

          this.countDocPerArea();
        } else {
          this.toastrService.error(res.message);
        }

        this.documentArchiveIsLoading = false;
      });
  }

  /**
   * Utility: extracts documents from one section of documentArchive
   * and show the documents into page
   * in getDocumentArchiveData()
   *
   * array of documents
   *
   * @since 1.0.0
   */
  getDocsFromSection(section): any {
    const docList = [];
    for (const doc in section.documents) {
      if (section.documents[doc].length) {
        section.documents[doc].forEach((item: any) => {
          if (!item.deleted) {
            if (
              item.data &&
              item.data.nomeSocietaEroganteInServizio &&
              // (item.data.rilasciatoFirmatoDa ||
              //   item.data.nomeDiRiferimento ||
              //   item.data.nomeSocietaEroganteInServizio ||
              //   item.data.nomeConduttore ||
              //   item.data.ragioneSocialeCostruttore ||
              //   item.data.daNome ||
              //   item.data.riferimentoComuneASL ||
              //   item.data.nomeAmministratoreDelCondominio ||
              //   item.data.nomeIdentificativoDellaReteDiTeleriscaldamento ||
              //   item.data.aFirmaDi ||
              //   item.data.nomeFornitore) &&
              typeof item.data.ragioneSocialeFornitore === "undefined"
            ) {
              if (item.data.rilasciatoFirmatoDa) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.rilasciatoFirmatoDa;
              } else if (item.data.nomeDiRiferimento) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.nomeDiRiferimento;
              } else if (item.data.nomeConduttore) {
                item.data["ragioneSocialeFornitore"] = item.data.nomeConduttore;
              } else if (item.data.ragioneSocialeCostruttore) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.ragioneSocialeCostruttore;
              } else if (item.data.daNome) {
                item.data["ragioneSocialeFornitore"] = item.data.daNome;
              } else if (item.data.riferimentoComuneASL) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.riferimentoComuneASL;
              } else if (item.data.nomeAmministratoreDelCondominio) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.nomeAmministratoreDelCondominio;
              } else if (
                item.data.nomeIdentificativoDellaReteDiTeleriscaldamento
              ) {
                item.data["ragioneSocialeFornitore"] =
                  item.data.nomeIdentificativoDellaReteDiTeleriscaldamento;
              } else if (item.data.aFirmaDi) {
                item.data["ragioneSocialeFornitore"] = item.data.aFirmaDi;
              } else if (item.data.nomeFornitore) {
                item.data["ragioneSocialeFornitore"] = item.data.nomeFornitore;
              } else {
                item.data["ragioneSocialeFornitore"] =
                  item.data.nomeSocietaEroganteInServizio;
              }
            }
            if (item.data && item.data.barCode) {
              if (item.data.barCode.length > 6) {
                item.data["docCode"] = item.data.barCode.substring(
                  item.data.barCode.length - 7,
                  item.data.barCode.length - 1
                );
              }
            }
            docList.push({
              title: this.translateService.instant(
                "properties.documents.titles." + doc
              ),
              type: "doc",
              docId: item._id,
              key: doc,
              mandatory:
                (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") ? false : item.mandatory,
              expired: item.expired,
              deleted: item.deleted,
              dateExpired: item.dateExpired,
              contract: item.contract,
              pdfmsg: item.pdfmsg,
              data: item.data,
              checked: false,
            });
          }
          if (!item.deleted && item.data) {
            this.insertedDocs.push({
              title: this.translateService.instant(
                "properties.documents.titles." + doc
              ),
              docId: item._id,
              data: item.data,
              checked: false,
            });
          }
        });
      }
    }

    docList.sort(this.compareExpired);
    return docList;
  }

  /**
   * Utility: extracts only contracts from one section of documentArchive
   * and show the documents into page
   * in getDocumentArchiveData()
   *
   * array of documents
   *
   * @since 1.0.0
   */
  getDocsFromContractSection(section, keyTranslateTitle): any {
    const docList = [];
    for (const doc in section.documents) {
      if (section.documents[doc].length) {
        section.documents[doc].forEach((item: any) => {
          if (item.contract) {
            if (!item.deleted) {
              if (
                item.data &&
                item.data.nomeSocietaEroganteInServizio &&
                // (item.data.rilasciatoFirmatoDa ||
                //   item.data.nomeDiRiferimento ||
                //   item.data.nomeSocietaEroganteInServizio ||
                //   item.data.nomeConduttore ||
                //   item.data.ragioneSocialeCostruttore ||
                //   item.data.daNome ||
                //   item.data.riferimentoComuneASL ||
                //   item.data.nomeAmministratoreDelCondominio ||
                //   item.data.nomeIdentificativoDellaReteDiTeleriscaldamento ||
                //   item.data.aFirmaDi ||
                //   item.data.nomeFornitore) &&
                typeof item.data.ragioneSocialeFornitore === "undefined"
              ) {
                if (item.data.rilasciatoFirmatoDa) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.rilasciatoFirmatoDa;
                } else if (item.data.nomeDiRiferimento) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeDiRiferimento;
                } else if (item.data.nomeConduttore) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeConduttore;
                } else if (item.data.ragioneSocialeCostruttore) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.ragioneSocialeCostruttore;
                } else if (item.data.daNome) {
                  item.data["ragioneSocialeFornitore"] = item.data.daNome;
                } else if (item.data.riferimentoComuneASL) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.riferimentoComuneASL;
                } else if (item.data.nomeAmministratoreDelCondominio) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeAmministratoreDelCondominio;
                } else if (
                  item.data.nomeIdentificativoDellaReteDiTeleriscaldamento
                ) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeIdentificativoDellaReteDiTeleriscaldamento;
                } else if (item.data.aFirmaDi) {
                  item.data["ragioneSocialeFornitore"] = item.data.aFirmaDi;
                } else if (item.data.nomeFornitore) {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeFornitore;
                } else {
                  item.data["ragioneSocialeFornitore"] =
                    item.data.nomeSocietaEroganteInServizio;
                }
              }
              if (item.data && item.data.barCode) {
                if (item.data.barCode.length > 6) {
                  item.data["docCode"] = item.data.barCode.substring(
                    item.data.barCode.length - 7,
                    item.data.barCode.length - 1
                  );
                }
              }
              docList.push({
                title: this.translateService.instant(
                  "properties.documents.titles." + doc
                ),
                type: "doc",
                docId: item._id,
                key: doc,
                mandatory:
                (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") ? false : item.mandatory,
                expired: item.expired,
                deleted: item.deleted,
                dateExpired: item.dateExpired,
                contract: item.contract,
                pdfmsg: item.pdfmsg,
                data: item.data,
                checked: false,
                keyTranslateTitle,
              });
            }
            // only contracts
            const contractSection = doc.substr(0, 2) === "HA" ? true : false;
            if (!item.deleted && item.data && contractSection) {
              this.insertedDocs.push({
                title: this.translateService.instant(
                  "properties.documents.titles." + doc
                ),
                docId: item._id,
                data: item.data,
                checked: false,
                keyTranslateTitle,
              });
            }
          }
        });
      }
    }

    docList.sort(this.compareExpired);
    return docList;
  }

  /**
   * Order docs by expired property
   *
   * @since 1.0.0
   */
  compareExpired(a, b): any {
    if (a.expired < b.expired) {
      return -1;
    } else if (a.expired > b.expired) {
      return 1;
    } else {
      return 0;
    }
  }

  /**
   * Count document per area and make visible the area in side menu.
   * Count mandatory, mandatory not inserted and generic document.
   * Show the counters into badge of side menu.
   * method used into getDocumentArchiveData()
   *
   * @since 1.0.0
   */
  countDocPerArea(): void {
    this.totMandatoryDocs = 0;
    this.totInsertDocs = 0;
    this.totNotInsertDocs = 0;
    this.totOptionalDocs = 0;
    this.totDocs = 0;

    // Only Privacy
    if (
      this.subscriptionType === 'PRIVACY' &&
      !this.loggedUserAdmin
    ) {
      this.documentsStructure = this.documentsStructure.filter(
        (el: DocumentInterface) => el.title === "properties.documents.gestionaleAmministrativo"
      );
      this.documentsStructure.forEach((el: any) => {
        if (Array.isArray(el.content)) {
          el.content = el.content.filter(
            (subEl: any) => 
              subEl.title === "properties.documents.Aprivacy" || 
              subEl.title === "properties.documents.gestAmmTivoDipendenti" ||
              subEl.title === "properties.documents.gestAmmTivoGestionali"
          );
        }
      });
    }

    // Hide all visible elements of side menu
    for (const key in this.documentsStructure) {
      this.documentsStructure[key].visible = false;
      if (Array.isArray(this.documentsStructure[key].content)) {
        this.documentsStructure[key].content.forEach((el: any) => {
          el.visible = false;
        });
      }
    }

    if (this.allDocs && Array.isArray(this.allDocs)) {
      this.allDocs.forEach((item: any) => {
        const title = item.keyTranslateTitle;
        const levels = item.content;
        if (
          title !== "properties.documents.unitaImmobiliari" &&
          title !== "properties.documents.attivitaSottopostePrevenzioneIncendi"
        ) {
          for (const key in this.documentsStructure) {
            if (this.documentsStructure[key].title === title) {
              this.documentsStructure[key].visible = true;

              let allStructuresDeleted = true;
              if (this.showStructureDeleted) {
                allStructuresDeleted = false;
              }
              levels.forEach((el: any) => {
                this.documentsStructure[key].content.forEach((subEl: any) => {
                  if (subEl.title === el.keyTranslateTitle) {
                    let allSubStructuresDeleted = true;
                    if (this.showStructureDeleted) {
                      allSubStructuresDeleted = false;
                    }

                    if (typeof el.active !== "undefined") {
                      // Only for "Gestione Dipendneti"
                      if (this.showStructureDeleted) {
                        // A deleted section left without rows is pruned from the archive by
                        // filterDisplayedDocs, so its voice would point at nothing.
                        subEl.visible = el.active ? true : this.hasAnyDocument(el);
                      } else {
                        subEl.visible = el.active;
                      }
                    } else {
                      subEl.visible = true;
                    }

                    subEl.notInsert = 0;
                    subEl.mandatory = 0;
                    subEl.optional = 0;
                    if (Array.isArray(el.content)) {
                      el.content.forEach((subSubEl: any) => {
                        if (
                          !this.showStructureDeleted &&
                          (typeof subSubEl.active === "undefined" ||
                            subSubEl.active)
                        ) {
                          allSubStructuresDeleted = false;
                        }
                        if (
                          this.showStructureDeleted ||
                          (!this.showStructureDeleted &&
                            (typeof subSubEl.active === "undefined" ||
                              subSubEl.active))
                        ) {
                          if (Array.isArray(subSubEl.content)) {
                            subSubEl.content.forEach((subSubSubEl: any) => {
                              if (subSubSubEl.mandatory) {
                                this.totMandatoryDocs++;
                                if (this.helperService.isDocumentDataPopulated(subSubSubEl.data)) {
                                  subEl.mandatory++;
                                  this.totInsertDocs++;
                                }
                              }
                              if (
                                subSubSubEl.mandatory &&
                                !this.helperService.isDocumentDataPopulated(subSubSubEl.data)
                              ) {
                                subEl.notInsert++;
                                this.totNotInsertDocs++;
                              }
                              if (!subSubSubEl.mandatory) {
                                subEl.optional++;
                                this.totOptionalDocs++;
                              }
                            });
                          } else {
                            if (subSubEl.mandatory) {
                              this.totMandatoryDocs++;
                              if (this.helperService.isDocumentDataPopulated(subSubEl.data)) {
                                subEl.mandatory++;
                                this.totInsertDocs++;
                              }
                            }
                            if (
                              subSubEl.mandatory &&
                              !this.helperService.isDocumentDataPopulated(subSubEl.data)
                            ) {
                              subEl.notInsert++;
                              this.totNotInsertDocs++;
                            }
                            if (!subSubEl.mandatory) {
                              subEl.optional++;
                              this.totOptionalDocs++;
                            }
                          }
                        }
                      });
                    }
                    if (allSubStructuresDeleted) {
                      subEl.visible = false;
                    } else {
                      allStructuresDeleted = false;
                    }
                  }
                });
              });

              if (allStructuresDeleted) {
                this.documentsStructure[key].visible = false;
              }
              break;
            }
          }
        } else if (title === "properties.documents.unitaImmobiliari") {
          for (const key in this.documentsStructure) {
            if (this.documentsStructure[key].title === title) {
              let allStructuresDeleted = true;
              if (this.showStructureDeleted) {
                allStructuresDeleted = false;
              }

              this.documentsStructure[key].content.length = 0;
              this.documentsStructure[key].visible = true;
              levels.forEach((el: any) => {
                const subEl: DocumentInterface = {
                  title: el.title,
                  type: "link",
                  section: "string",
                  key: "",
                  visible: true,
                  notInsert: 0,
                  mandatory: 0,
                  optional: 0,
                };
                // With the toggle on, a deleted unit is listed only while it still owns rows:
                // filterDisplayedDocs prunes the empty ones from the archive, so the voice
                // would point at a section the page does not render.
                if (
                  (this.showStructureDeleted &&
                    (el.active || this.hasAnyDocument(el))) ||
                  (!this.showStructureDeleted && el.active)
                ) {
                  allStructuresDeleted = false;
                  if (Array.isArray(el.content)) {
                    el.content.forEach((subSubEl: any) => {
                      if (Array.isArray(subSubEl.content)) {
                        subSubEl.content.forEach((subSubSubEl: any) => {
                          if (subSubSubEl.mandatory) {
                            this.totMandatoryDocs++;
                            if (this.helperService.isDocumentDataPopulated(subSubSubEl.data)) {
                              subEl.mandatory++;
                              this.totInsertDocs++;
                            }
                          }
                          if (
                            subSubSubEl.mandatory &&
                            !this.helperService.isDocumentDataPopulated(subSubSubEl.data)
                          ) {
                            subEl.notInsert++;
                            this.totNotInsertDocs++;
                          }
                          if (!subSubSubEl.mandatory) {
                            subEl.optional++;
                            this.totOptionalDocs++;
                          }
                        });
                      } else {
                        if (subSubEl.mandatory) {
                          this.totMandatoryDocs++;
                          if (this.helperService.isDocumentDataPopulated(subSubEl.data)) {
                            subEl.mandatory++;
                            this.totInsertDocs++;
                          }
                        }
                        if (
                          subSubEl.mandatory &&
                          !this.helperService.isDocumentDataPopulated(subSubEl.data)
                        ) {
                          subEl.notInsert++;
                          this.totNotInsertDocs++;
                        }
                        if (!subSubEl.mandatory) {
                          subEl.optional++;
                          this.totOptionalDocs++;
                        }
                      }
                    });
                  }

                  this.documentsStructure[key].content.push(subEl);
                }
              });

              if (allStructuresDeleted) {
                this.documentsStructure[key].visible = false;
              }
              break;
            }
          }
        } else if (
          title === "properties.documents.attivitaSottopostePrevenzioneIncendi"
        ) {
          for (const key in this.documentsStructure) {
            if (this.documentsStructure[key].title === title) {
              let allStructuresDeleted = true;
              if (this.showStructureDeleted) {
                allStructuresDeleted = false;
              }

              this.documentsStructure[key].content.length = 0;
              this.documentsStructure[key].visible = true;
              if (Array.isArray(levels[0].content)) {
                levels[0].content.forEach((el: any) => {
                  if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
                    const subEl: any = {
                      title: levels[0].keyTranslateTitle,
                      translation: (this.documentsStructure[key] as any).translation,
                      type: "link",
                      section: "string",
                      key: "",
                      visible: true,
                      notInsert: 0,
                      mandatory: 0,
                      optional: 0,
                    };
                    allStructuresDeleted = false;
                    if (el.mandatory) {
                      this.totMandatoryDocs++;
                      if (this.helperService.isDocumentDataPopulated(el.data)) {
                        subEl.mandatory++;
                        this.totInsertDocs++;
                      }
                    }
                    if (
                      el.mandatory &&
                      !this.helperService.isDocumentDataPopulated(el.data)
                    ) {
                      subEl.notInsert++;
                      this.totNotInsertDocs++;
                    }
                    if (!el.mandatory) {
                      subEl.optional++;
                      this.totOptionalDocs++;
                    }
                    this.documentsStructure[key].content.push(subEl);
                  } else {
                    const subEl: DocumentInterface = {
                      title: el.title,
                      type: "link",
                      section: "string",
                      key: "",
                      visible: true,
                      notInsert: 0,
                      mandatory: 0,
                      optional: 0,
                    };
                    // Same rule as the property units above: with the toggle on a deleted
                    // structure keeps its voice only while it still owns rows. Here the
                    // archive is even stricter, documentTypes.ts drops the node when the
                    // section has no documents at all.
                    if (
                      (this.showStructureDeleted &&
                        (el.active || this.hasAnyDocument(el))) ||
                      (!this.showStructureDeleted && el.active)
                    ) {
                      allStructuresDeleted = false;
                      if (Array.isArray(el.content)) {
                        el.content.forEach((subSubEl: any) => {
                          if (Array.isArray(subSubEl.content)) {
                            subSubEl.content.forEach((subSubSubEl: any) => {
                              if (subSubSubEl.mandatory) {
                                this.totMandatoryDocs++;
                                if (this.helperService.isDocumentDataPopulated(subSubSubEl.data)) {
                                  subEl.mandatory++;
                                  this.totInsertDocs++;
                                }
                              }
                              if (
                                subSubSubEl.mandatory &&
                                !this.helperService.isDocumentDataPopulated(subSubSubEl.data)
                              ) {
                                subEl.notInsert++;
                                this.totNotInsertDocs++;
                              }
                              if (!subSubSubEl.mandatory) {
                                subEl.optional++;
                                this.totOptionalDocs++;
                              }
                            });
                          } else {
                            if (subSubEl.mandatory) {
                              this.totMandatoryDocs++;
                              if (this.helperService.isDocumentDataPopulated(subSubEl.data)) {
                                subEl.mandatory++;
                                this.totInsertDocs++;
                              }
                            }
                            if (
                              subSubEl.mandatory &&
                              !this.helperService.isDocumentDataPopulated(subSubEl.data)
                            ) {
                              subEl.notInsert++;
                              this.totNotInsertDocs++;
                            }
                            if (!subSubEl.mandatory) {
                              subEl.optional++;
                              this.totOptionalDocs++;
                            }
                          }
                        });
                      }

                      this.documentsStructure[key].content.push(subEl);
                    }
                  }
                });
              }

              if (allStructuresDeleted) {
                this.documentsStructure[key].visible = false;
              }
              break;
            }
          }
        }
      });
    }

    this.progressBarVal = 0;
    if (this.totInsertDocs + this.totNotInsertDocs > 0) {
      this.progressBarVal = Math.trunc(
        (this.totInsertDocs / (this.totInsertDocs + this.totNotInsertDocs)) *
          100
      );
    }

    this.progressBarValStyle = { width: `${this.progressBarVal}%` };

    // Only Privacy
    if (
      this.subscriptionType === 'PRIVACY' &&
      !this.loggedUserAdmin
    ) {
      this.totDocs = this.totInsertDocs + this.totOptionalDocs
    } else {
      // this.totDocs = this.totOptionalDocs + this.totInsertDocs;
      this.totDocs = this.property.get("totalDocsInserted");
      this.subscriptions.push(
        this.propertiesService.get(this.propertyId).subscribe(
          (res) => {
            if (res.status) {
              this.property.setData(res.data);
              this.totDocs = res.data.totalDocsInserted;
              return;
            }
          },
          (err) => {
            // if we came here users with given userId do not exist
          }
        )
      );
    }

    if (this.loggedUserShared || this.loggedUserTemporary) {
      for (const key in this.documentsStructure) {
        switch (this.documentsStructure[key].title) {
          case "properties.documents.gestionaleAmministrativo":
            if (!this.currentRole.propertiesPermission.gestionaleAmministrativo) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.verbaliconvocazioni":
            if (!this.currentRole.propertiesPermission.gestionaleContabile) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.urbanisticaEdEdilizia":
            if (!this.currentRole.propertiesPermission.urbanisticaEdEdilizia) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.catasto":
            if (!this.currentRole.propertiesPermission.catasto) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.locazioneContrdiLocazione":
            if (!this.currentRole.propertiesPermission.contratti) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.contratti":
            if (!this.currentRole.propertiesPermission.contratti) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.lineaVita":
            if (!this.currentRole.propertiesPermission.lineaVita) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.amiantoDoc":
            if (!this.currentRole.propertiesPermission.amiantoDoc) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.edile":
            if (!this.currentRole.propertiesPermission.edile) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.impiantoElettrico":
            if (!this.currentRole.propertiesPermission.impiantoElettrico) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.riscaldamentoCondizionamento":
            if (!this.currentRole.propertiesPermission.riscaldamentoCondizionamento) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.ascensoriEMontauto":
            if (!this.currentRole.propertiesPermission.ascensori) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.trattamentoAcqua":
            if (!this.currentRole.propertiesPermission.trattamentoAcqua) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.impiantiElettronici":
            if (!this.currentRole.propertiesPermission.impiantiElettronici) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.attivitaSottopostePrevenzioneIncendi":
            if (!this.currentRole.propertiesPermission.antincendio) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.impiantiAntincendio":
            if (!this.currentRole.propertiesPermission.antincendio) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.impiantiSpeciali":
            if (!this.currentRole.propertiesPermission.impiantiSpeciali) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.impiantiRicreativiSportivi":
            if (!this.currentRole.propertiesPermission.impiantiRicreativiSportivi) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.manutenzioni":
            if (!this.currentRole.propertiesPermission.preventivi) {
              this.documentsStructure[key].visible = false;
            }
            break;
          case "properties.documents.unitaImmobiliari":
            if (!this.currentRole.propertiesPermission.unitaImmobiliari) {
              this.documentsStructure[key].visible = false;
            }
            break;
        }
      }
    }
  }

  /**
   * Handle insert doc dialog component
   *
   * @since 1.0.0
   */
  onInsertDocDialogOpen(docItem: any): void {
    let barCode = "";
    if (docItem && docItem.barCode) {
      barCode = docItem.barCode;
    } else {
      const total = (+this.property.get("totalDocsProgressive") + 1).toString();
      barCode = `${this.property.getId()}S${total.padStart(6, "0")}C`;
    }
    // const selectedArea = this.documentTypeStructure.getAreaFromChild(
    //   docItem.keyTranslateTitle
    // );
    const selectedArea = this.documentTypeStructure.getAreaFromChild2(
      docItem.keyTranslateTitle,
      this.documentTemplate
    );


    const dialogRef = this.dialog.open(
      PropertyDetailDocumentInsertDialogComponent,
      {
        data: {
          modalTitle: this.translateService.instant(
            "properties.addDocumentsPage.documentsTab.title"
          ),
          documentArchive: this.documentArchive,
          documentArchiveId: this.documentArchiveId,
          condoStructure: this.condoStructure,
          subscriptionType: this.subscriptionType,
          key: docItem.keyTranslateTitle.split("properties.documents.")[1],
          barCode,
          selectedArea,
          propertyId: this.propertyId,
          property: this.property,
        },
        width: "70vw",
        minHeight: "500px",
      }
    );

    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            // recreate view of all docs to show new inserted doc
            this.allDocs = [];
            if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
              this.getDocumentArchiveDataFree();
            } else {
              this.getDocumentArchiveData();
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle click on document and open dialog box to edit/show document data
   * 
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   * action: edit or show doc data
   * documentFather: 
   * keyTranslateTitle: 
   * structureId: related structure Id
   * expired: 
   * contractPdfMsg: 
   * sameCodeTargets: "edit" only. Left out by the template, which makes the method first offer the
   *   other rows with the same code (see selectSameCodeDocsToEdit); null edits the clicked row alone;
   *   a list saves the form on each of those rows.
   *
   * @since 1.0.0
   */
  onToggleEditShowDocument(
    modalTitle: string,
    documentKey: string,
    documentId: string,
    subDocumentId: string,
    itemData: any,
    action: string,
    documentFather?: string,
    keyTranslateTitle?: string,
    structureId?: string,
    expired?: boolean,
    contractPdfMsg?: any,
    topic?: string,
    sameCodeTargets?: SameCodeEditTarget[] | null,
  ): void {
    // if (this.subscriptionType === "FREE") {
    //   documentId = "undefined";
    // }
    // Create formly default fields
    const defaultFields = [
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "textarea",
        key: "contenutoDocumento",
        templateOptions: {
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.contenutoDocumento"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.contenutoDocumento"
          ),
          required: true,
          minLength: 2,
          maxLength: 500,
          rows: 5,
          disabled: action === "show" ? true : false,
        },
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "input",
        key: "protocollo",
        templateOptions: {
          readonly:false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          required: false,
          minLength: 0,
          maxLength: 500,
        },
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "textarea",
        key: "keyword",
        templateOptions: {
          readonly:false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          required: false,
          minLength: 0,
          maxLength: 1000,
          rows: 3
        },
      }
    ];

    // create fields formly from document key: es AA1
    // const aryfields = this.documentTypeStructure.getFormlyFields(
    //   documentKey,
    //   false
    // );
    const schemaType = JSON.parse(
      JSON.stringify(this.documentTypeStructure.getDocumentType2(documentKey, this.documentTemplate))
    );
    const aryfields = this.documentTypeStructure.getFormlyFields2(
      schemaType,
      false
    );
    aryfields.forEach((item: any) => {
      if (item.templateOptions && item.templateOptions.placeholder) {
        item.templateOptions.placeholder = this.translateService.instant(
          item.templateOptions.placeholder
        );
      }
      if (item.type === "select") {
        if (Array.isArray(item.templateOptions.options)) {
          item.templateOptions.options.forEach((subItem: any) => {
            subItem.label = this.translateService.instant(subItem.label);
          });
        }
      }
      if (item.type === "selectNew") {
        item.type = "autocomplete";
        if (item.templateOptions.initialValues) {
          const options: any[] = [];
          switch (item.templateOptions.initialValues) {
            case "suppliersName":
              this.suppliersName.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "yearsOfStorage":
              this.yearsOfStorage.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "conductorName":
              this.conductorName.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
            case "nameInterventionArchiving":
              this.nameInterventionArchiving.forEach((subItem: any) => {
                // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
              });
              break;
          }
          item.templateOptions.initialValues = options;
          item.templateOptions.addTagText = this.translateService.instant(
            item.templateOptions.addTagText
          );
          item.templateOptions.customLabel = this.translateService.instant(
            item.templateOptions.customLabel
          );
        }
      }
      if (
        item.type === "autocomplete" ||
        item.type === "autocomplete-supplier"
      ) {
        item.templateOptions.addTagText = this.translateService.instant(
          item.templateOptions.addTagText
        );
        item.templateOptions.customLabel = this.translateService.instant(
          item.templateOptions.customLabel
        );
        if (item.templateOptions.search$ === "suppliersService") {
          item.templateOptions.search$ =
            this.suppliersService.getListV2.bind(this);
        }
      } else {
        if (item.templateOptions && item.templateOptions.label) {
          item.templateOptions.label = this.translateService.instant(
            item.templateOptions.label
          );
        }
      }

      if (action === "show" && item.templateOptions) {
        item.templateOptions.disabled = true;
      }
    });

    // const contract = this.documentTypeStructure.isContract(documentKey);
    const contract = this.documentTypeStructure.isContract2(documentKey, this.documentTemplate);

    const fieldGroup = aryfields.concat(defaultFields);

    const itemConfig = [
      {
        fieldGroupClassName: "row",
        fieldGroup: fieldGroup,
      },
    ];

    const propertyCode = this.propertyData.code;
    const companyCode = this.company.get("code");

    let barCode = "";
    if (itemData && itemData.barCode) {
      barCode = itemData.barCode;
    } else {
      // just for Paper doc
      const total = (+this.property.get("totalDocsProgressive") + 1).toString();
      barCode = `${this.property.getId()}S${total.padStart(6, "0")}C`;
    }

    // const selectedArea =
    //   this.documentTypeStructure.getAreaFromChild(keyTranslateTitle);
    const selectedArea =
      this.documentTypeStructure.getAreaFromChild2(keyTranslateTitle, this.documentTemplate);

    let title;
    if (keyTranslateTitle) {
      title = keyTranslateTitle.split("properties.documents.")[1];
    }
    if (this.subscriptionType !== "FREE" && this.subscriptionType !== "FREEBASIC") {
      //edilecanneFumarieColl
      for (const docs of this.documentTemplate) {
        if (!docs.deleted) {
          const content = docs.content;
          if (content && Array.isArray(content)) {
            const index = content.findIndex((subDocs: any) => {
              return (
                subDocs.title.replace("properties.documents.", "") === title
              );
            });
            if (index > -1) {
              const structure = content[index].structure;
              if (structure && structure !== "") {
                this.condoStructure[structure].forEach((item: any) => {
                  if (
                    typeof item.active !== "undefined" &&
                    item.active
                  ) {
                    const ids = this.structureSelected.map(
                      (el) => el.id
                    );
                    let name = item.nome;
                    switch (structure) {
                      case "dipendenti":
                        name = `${item.nome} ${item.cognome}`;
                        break;
                      case "contatoreGas":
                        name = `${item.nome} - ${item.codicePdr}`;
                        break;
                      case "ascensore":
                        name = `${item.nome} - ${item.numeroMatricola}`;
                        break;
                      case "montauto":
                        name = `${item.nome} - ${item.numeroMatricola}`;
                        break;
                      case "contatoriAcqua":
                        name = `${item.nome} - ${item.codiceUtente}`;
                        break;
                      // case "estintori":
                      //   name = "Estintori";
                      //   break;
                    }
                    const selStructure = {
                      id: item._id,
                      name,
                      value: false,
                      type: structure,
                    };
                    // Prevent duplicate insert
                    if (ids.includes(selStructure.id)) {
                      itemData = {
                        ...itemData,
                        structureSelected:
                          this.structureSelected.filter(
                            (el) => el.type === selStructure.type
                          ),
                      };
                      return;
                    }
                    this.structureSelected.push(selStructure);
                    itemData = {
                      ...itemData,
                      structureSelected: this.structureSelected.filter(
                        (el) => el.type === selStructure.type
                      ),
                    };
                  }
                });
              } else {
                // vvFdefinitivo
                if (title === "vvFdefinitivo") {
                  if (
                    this.documentArchive.vvFdefinitivo &&
                    Array.isArray(this.documentArchive.vvFdefinitivo)
                  ) {
                    this.documentArchive.vvFdefinitivo.forEach((item: any) => {
                      if (item.structureObjectType !== "") {
                        const ids = this.structureSelected.map((el) => el.id);
                        const structure = {
                          id: item.structureObjectId,
                          name: item.structureObjectName,
                          value: false,
                          type: "vvFdefinitivo",
                          typeStructure: item.structureObjectType,
                        };
                        // Prevent duplicate insert
                        if (ids.includes(structure.id)) {
                          itemData = {
                            ...itemData,
                            structureSelected: this.structureSelected.filter(
                              (el) => el.type === structure.type
                            ),
                          };
                          return;
                        }
                        this.structureSelected.push(structure);
                        itemData = {
                          ...itemData,
                          structureSelected: this.structureSelected.filter(
                            (el) => el.type === structure.type
                          ),
                        };
                      }
                    });
                  }
                } else if (title === "contratti" && documentKey === "HA12") {
                  this.condoStructure['fornituraBt'].forEach((item: any) => {
                    if (
                      typeof item.active !== "undefined" &&
                      item.active &&
                      item._id.toString() === structureId.toString()
                    ) {
                      const ids = this.structureSelected.map(
                        (el) => el.id
                      );
                      const selStructure = {
                        id: item._id,
                        name: 'fornituraBt',
                        value: true,
                        type: 'fornituraBt',
                      };
                      // Prevent duplicate insert
                      if (ids.includes(selStructure.id)) {
                        itemData = {
                          ...itemData,
                          structureSelected:
                            this.structureSelected.filter(
                              (el) => el.type === selStructure.type
                            ),
                        };
                        return;
                      }
                      this.structureSelected.push(selStructure);
                      itemData = {
                        ...itemData,
                        structureSelected: this.structureSelected.filter(
                          (el) => el.type === selStructure.type
                        ),
                      };
                    }
                  });
                } else if (title === "contratti" && documentKey === "HA64") {
                  this.condoStructure['fornituraMt'].forEach((item: any) => {
                    if (
                      typeof item.active !== "undefined" &&
                      item.active &&
                      item._id.toString() === structureId.toString()
                    ) {
                      const ids = this.structureSelected.map(
                        (el) => el.id
                      );
                      const selStructure = {
                        id: item._id,
                        name: 'fornituraMt',
                        value: true,
                        type: 'fornituraMt',
                      };
                      // Prevent duplicate insert
                      if (ids.includes(selStructure.id)) {
                        itemData = {
                          ...itemData,
                          structureSelected:
                            this.structureSelected.filter(
                              (el) => el.type === selStructure.type
                            ),
                        };
                        return;
                      }
                      this.structureSelected.push(selStructure);
                      itemData = {
                        ...itemData,
                        structureSelected: this.structureSelected.filter(
                          (el) => el.type === selStructure.type
                        ),
                      };
                    }
                  });
                }
              }
              break;
            }
          }
        }
      }
    } else {
      switch (title) {
        case "contratti":
          // this.contratti = true;
          itemData = {
            ...itemData,
            structureSelected: "-1",
          };
          break;
        case "gestAmmTivoDipendenti":
        case "lineaVita":
        case "impeleGruppoElettrogeno":
        case "impeleFotovoltaico":
        case "impelescaricheAtmosferiche":
        case "riscaldrisccentraliz":
        case "serbatoioAGasolio":
        case "riscaldgasMetcontatrete":
        case "riscaldsottostazriscaldamen":
        case "riscaldcanneFumariePerCt":
        case "riscaldsolareTermico":
        case "riscaldcentrteleriscaldamen":
        case "riscGeotermicoPozzi":
        case "riscGeotermicoSonde":
        case "riscaldsoloPompeDiCalore":
        case "riscaldcentrRicevBsc":
        case "riscaldimpcondizionamento":
        case "ascensoriEMontautoAscensori":
        case "ascensoriEMontautoMontauto":
        case "tratAcquaContatoreAcqua":
        case "tratAcquaCentraleIdrica":
        case "tratAcquaSottAlimDaBsc":
        case "tratAcquaPompSollacqpiov":
        case "impElettronicitvTerrestre":
        case "impElettronicitvSatellitare":
        case "impElettroniciautomazioni":
        case "impElettroniciimpCitofonico":
        case "impElettroniciimpiantoTvcc":
        case "impElettroniciantintrusione":
        case "vvFdefinitivo":
        case "incendioEstintori":
        case "incendioReteIdranti":
        case "incendioReteSprinkler":
        case "incendioStazionePompaggio":
        case "incendioRilevazione":
        case "incendioVentilazione":
        case "impiantiSpeciali":
        case "impricreativipiscina":
        case "impricreativipalestra":
        case "impricreativicampoTennis":
        case "impricreativicampoCalcio":
        case "impricreativiparcoGiochi":
        case "unitaimmvvf":
        case "unitaImmdocAmm":
        case "unitaimmelettrico":
        case "unitaimmantintrusione":
        case "unitaimmidricoSanitari":
        case "unitaimmtermico":
        case "unitaimmcondizionamento":
        case "unitaimmape":
        case "unitaimmelaboratiGrafici":
        case "unitaimmcanneFumariePer":
        case "unitaImmsicurezza":
          itemData = {
            ...itemData,
            structureSelected: "-1",
          };
          break;
        default:
          itemData = {
            ...itemData,
            structureSelected: "",
          };
      }
      structureId = undefined;
    }

    // "Modifica dati aggiuntivi" on a code that other rows share: offer them before the form opens.
    // The choice comes back into this method with the rows to save, so the form is built as usual.
    if (action === "edit" && typeof sameCodeTargets === "undefined") {
      this.selectSameCodeDocsToEdit(
        subDocumentId,
        documentKey,
        itemData,
        fieldGroup,
        structureId !== "" ? structureId : undefined,
        (targets: SameCodeEditTarget[] | null) =>
          this.onToggleEditShowDocument(
            modalTitle,
            documentKey,
            documentId,
            subDocumentId,
            itemData,
            action,
            documentFather,
            keyTranslateTitle,
            structureId,
            expired,
            contractPdfMsg,
            topic,
            targets
          )
      );
      return;
    }

    // this.structureSelected = this.structureSelected.find(el => el.id === structureId);
    let dialogRef: any;
    // Admins can choose between paper or digital document insertion
    if (
      action === "new" &&
      (this.loggedUserRole.type === "admin" ||
        this.loggedUserRole.type === "operator")
    ) {
      dialogRef = this.dialog.open(DocumentPaperInsertDialogComponent, {
        data: {
          modalTitle,
          documentArchiveId: this.documentArchiveId,
          documentTypeRoute:
            this.documentTypeStructure.getDocRoute2(documentKey, this.documentTemplate),
          documentId,
          documentKey,
          subDocumentId,
          itemData,
          itemConfig,
          propertyCode,
          companyCode,
          barCode,
          action,
          documentFather,
          keyTranslateTitle: title,
          structureId,
          contract,
          expired,
          contractPdfMsg,
          selectedArea,
          property: this.propertyData,
          topic,
          //docNumber: this.propertyData.totalDocsInserted ? this.propertyData.totalDocsInserted : 0,
        },
        width: "700px",
      });
    } else {
      dialogRef = this.dialog.open(PropertyDetailDocumentItemDialogComponent, {
        data: {
          modalTitle,
          documentArchiveId: this.documentArchiveId,
          documentTypeRoute:
            this.documentTypeStructure.getDocRoute2(documentKey, this.documentTemplate),
          documentId,
          documentKey,
          subDocumentId,
          itemData,
          itemConfig,
          propertyCode,
          companyCode,
          barCode,
          action,
          documentFather,
          keyTranslateTitle: title,
          structureId: structureId !== '' ? structureId : undefined,
          contract,
          expired,
          contractPdfMsg,
          selectedArea,
          property: this.propertyData,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: this.propertyName,
          sameCodeTargets: sameCodeTargets || undefined,
          //docNumber: this.propertyData.totalDocsInserted ? this.propertyData.totalDocsInserted : 0,
        },
        width: "700px",
      });
    }

    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            // return;
          } else {
            // recreate view of all docs to show new inserted doc
            this.allDocs = [];
            if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
              this.getDocumentArchiveDataFree();
            } else {
              this.getDocumentArchiveData();
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * "Modifica dati aggiuntivi" on a document whose code (barcode) other rows share: let the user
   * tick which of those rows receive the edit before the form opens.
   *
   * Every row with that code keeps its own copy of the additional data, and the form shows the data
   * of the clicked row. A row whose data differ, or whose type has other fields, cannot be ticked:
   * saving the form on it would overwrite data the user never saw. Nothing is ticked on opening.
   *
   * `proceed` gets the rows to save, or null to edit the clicked row alone as before: no code, no
   * other row with it, only the clicked row ticked, or a failed lookup. It is not called when the
   * user closes the selection, nor for a second click while the lookup runs.
   *
   * @param subDocumentId `_id` of the clicked row on the page (`subDocId` of its flat row)
   * @param documentKey type of the clicked row, e.g. AA1
   * @param itemData additional data of the clicked row
   * @param formFields fields of the edit form, labels already translated
   * @param clickedStructureId structure segment the single edit of the clicked row sends
   * @param proceed continues the edit with the chosen rows
   *
   * @since 3.0.0
   */
  private selectSameCodeDocsToEdit(
    subDocumentId: string,
    documentKey: string,
    itemData: any,
    formFields: any[],
    clickedStructureId: string | undefined,
    proceed: (targets: SameCodeEditTarget[] | null) => void
  ): void {
    if (this.sameCodeLookupInProgress) {
      return;
    }
    const barCode =
      itemData && typeof itemData.barCode === "string" ? itemData.barCode.trim() : "";
    if (barCode === "" || !this.documentArchiveId) {
      proceed(null);
      return;
    }

    this.sameCodeLookupInProgress = true;
    this.subscriptions.push(
      this.documentArchivesService
        // The code is a path segment: a free paper prefix must not be able to change the route
        .getDocsByBarcode(this.documentArchiveId, encodeURIComponent(barCode))
        .pipe(finalize(() => (this.sameCodeLookupInProgress = false)))
        .subscribe(
          (res) => {
            if (!res || !res.status) {
              this.onSameCodeLookupFailed(proceed);
              return;
            }
            const choices = buildSameCodeChoices({
              rows: res.data,
              barCode,
              clickedSubDocId: subDocumentId,
              clickedKey: documentKey,
              fields: collectSameCodeFormFields(formFields),
              formOf: (key: string) => ({
                schemaType: this.documentTypeStructure.getDocumentType2(key, this.documentTemplate),
                route: this.documentTypeStructure.getDocRoute2(key, this.documentTemplate),
              }),
            });
            if (!choices || choices.length < 2) {
              proceed(null);
              return;
            }
            const dialogData: SameCodeEditDialogData = { barCode, choices };
            this.dialog
              .open(PropertySameCodeEditDialogComponent, { data: dialogData, width: "700px" })
              .beforeClosed()
              .pipe(take(1))
              .subscribe((result: SameCodeEditDialogResult | undefined) => {
                const selected = result && Array.isArray(result.selected) ? result.selected : [];
                if (!selected.length) {
                  return;
                }
                if (selected.length === 1 && selected[0].isClicked) {
                  // The clicked row alone: exactly the edit of a single document
                  proceed(null);
                  return;
                }
                proceed(
                  buildSameCodeEditTargets(selected, {
                    clickedStructureId,
                    sendStructureIds:
                      this.subscriptionType !== "FREE" && this.subscriptionType !== "FREEBASIC",
                    routeOf: (key: string) =>
                      this.documentTypeStructure.getDocRoute2(key, this.documentTemplate),
                  })
                );
              });
          },
          // The service has already shown the error
          () => this.onSameCodeLookupFailed(proceed)
        )
    );
  }

  /**
   * The rows sharing the code could not be read: say so, then edit the clicked row alone
   *
   * @since 3.0.0
   */
  private onSameCodeLookupFailed(proceed: (targets: SameCodeEditTarget[] | null) => void): void {
    this.toastrService.warning(
      this.translateService.instant("properties.dialog.docEditSameCodeLookupFailed")
    );
    proceed(null);
  }

  /**
   * Handle click on document form to download file
   * itemData: data value of form items
   * row: table row the files belong to; its ids let the dialog offer "Scarica tutti" (one zip)
   *
   * @since 1.0.0
   */
  onToggleDownloadDocument(documents: any, row?: any): void {
    if (!documents || !documents.length) {
      console.warn("ERROR: NO DOCS");
      return;
    }
    const dialogRef = this.dialog.open(DocumentDownloadDialogComponent, {
      data: {
        propertyId: this.propertyId,
        documents,
        // `docId` of a row is the `_id` GET document-archives/:id returns for it: the zip endpoint expects it
        documentArchiveId: this.documentArchiveId,
        docId: row && row.docId ? row.docId : "",
        barCode: row && row.data && row.data.barCode ? row.data.barCode : "",
      },
      width: "600px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        } else {
          // recreate view of all docs to show new inserted doc
          this.allDocs = [];
          if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
            this.getDocumentArchiveDataFree();
          } else {
            this.getDocumentArchiveData();
          }
        }
      });
  }

  /**
   * Handle click on document form to delete file
   * itemData: data value of the document
   *
   * @since 1.0.0
   */
  onToggleDeleteDocument(
    item: any
  ): void {
    const dialogRef = this.dialog.open(
      PropertyDeleteDocumentItemDialogComponent,
      {
        data: {
          documentArchiveId: this.documentArchiveId,
          barCode: item.data.barCode ? item.data.barCode : '',
          mockup: item.mockup ? item.mockup : false,
          item,
        },
        width: "600px",
      }
    );
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            // recreate view of all docs to show new inserted doc
            this.allDocs = [];
            if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
              this.getDocumentArchiveDataFree();
            } else {
              this.getDocumentArchiveData();
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }
  
  /**
   * Show QRcode section
   *
   * @since 1.0.0
   */
  onTogglePrintSectionQRcode(item: any, docItem?: any, subDocItem?: any): void {
    const name = this.propertyData.name;
    const city = this.propertyData.address.city;
    const address = `${this.propertyData.address.route} ${this.propertyData.address.streetNumber}`;

    let structureId = "";
    if (subDocItem && subDocItem.structureId && subDocItem.structureId !== "") {
      structureId = subDocItem.structureId;
    }

    let selectedArea: any;
    if (
      item &&
      item.content &&
      Array.isArray(item.content) &&
      item.content.length
    ) {
      if (item.content[0].keyTranslateTitle) {
        // const areaNumber = this.documentTypeStructure.getAreaFromChild(
        //   item.content[0].keyTranslateTitle
        // );
        const areaNumber = this.documentTypeStructure.getAreaFromChild2(
          item.content[0].keyTranslateTitle,
          this.documentTemplate
        );
        
        selectedArea = this.documentTypeStructure.getDocumentArea2(
          areaNumber.toString(),
          this.documentTemplate
        );
      }
    }

    let subStructureRoute = "";
    let subStructure = "";
    if (docItem) {
      subStructureRoute = docItem.keyTranslateTitle.replace(
        "properties.documents.",
        ""
      );
      subStructure = docItem.title;
    }


    const documentTypeRoute = item.keyTranslateTitle.replace(
      "properties.documents.",
      ""
    );

    this.dialog.open(PropertyDetailDocumentItemPrintDialogComponent, {
      data: {
        modalTitle: item.title,
        documentArchiveId: this.documentArchiveId,
        documentTypeRoute,
        documentId: "",
        documentKey: "",
        subDocumentId: "",
        itemData: "",
        propertyCode: "",
        companyCode: "",
        barCode: "",
        name,
        city,
        address,
        area: "",
        topic: "",
        structure: "",
        subArea: selectedArea && selectedArea.title ? selectedArea.title : "",
        subStructureRoute,
        subStructure,
        structureId,
      },
      width: "700px",
    });
  }

  /**
   * Handle click on document form to print file
   * area: document area
   * topic: document topic
   * type: document type
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   *
   * @since 1.0.0
   */
  onTogglePrintDocument(
    subArea: string,
    area: string,
    topic: string,
    structure: string,
    modalTitle: string,
    documentKey: string,
    documentId: string,
    subDocumentId: string,
    itemData: any
  ): void {
    // key | docId | subDocId
    const propertyCode = this.propertyData.code;
    const companyCode = this.company.get("code");

    // const areaNumber = this.documentTypeStructure.getAreaFromChild(subArea);
    const areaNumber = this.documentTypeStructure.getAreaFromChild2(subArea, this.documentTemplate);
    const selectedArea = this.documentTypeStructure.getDocumentArea2(
      areaNumber.toString(),
      this.documentTemplate
    );

    let barCode = "";
    if (itemData && itemData.barCode) {
      barCode = itemData.barCode;
    }
    const name = this.propertyData.name;
    const city = this.propertyData.address.city;
    const address = `${this.propertyData.address.route} ${this.propertyData.address.streetNumber}`;

    this.dialog.open(PropertyDetailDocumentItemPrintDialogComponent, {
      data: {
        modalTitle,
        documentArchiveId: this.documentArchiveId,
        documentTypeRoute: this.documentTypeStructure.getDocRoute2(documentKey, this.documentTemplate),
        documentId,
        documentKey,
        subDocumentId,
        itemData,
        propertyCode,
        companyCode,
        barCode,
        name,
        city,
        address,
        area,
        topic,
        structure,
        subArea: selectedArea && selectedArea.title ? selectedArea.title : "",
      },
      width: "700px",
    });
  }

  /**
   * Handle click on document form to print nodule
   * area: document area
   * topic: document topic
   * type: document type
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   *
   * @since 1.0.0
   */
  onTogglePrintModuleDoc(
    subArea: string,
    structure: string,
    modalTitle: string,
    itemData: any
  ): void {
    // const areaNumber = this.documentTypeStructure.getAreaFromChild(subArea);
    const areaNumber = this.documentTypeStructure.getAreaFromChild2(subArea, this.documentTemplate);
    const selectedArea = this.documentTypeStructure.getDocumentArea2(
      areaNumber.toString(),
      this.documentTemplate
    );

    subArea = subArea.replace("properties.documents.", "");
    let subAreaTitle = "";
    if (selectedArea && selectedArea.content && Array.isArray(selectedArea.content)) {
      for (const el of selectedArea.content)  {
        if (el.content && Array.isArray(el.content)) {
          for (const subEl of el.content) {
            if (subEl.title && subEl.title.replace("properties.documents.", "") === subArea) {
              subAreaTitle = subEl.translation[this.lang];
            }
          }
        }
      }
    }
    
    this.dialog.open(PropertyDetailDocumentPrintModuleDialogComponent, {
      data: {
        modalTitle,
        itemData,
        property: this.property,
        area: selectedArea && selectedArea.title ? selectedArea.title : "",
        docs: [{ title: modalTitle, subDoc: subArea, subAreaTitle }],
        structure: structure ? [{ name: structure }] : [],
      },
      width: "700px",
    });
  }

  /**
   * Handle side menu to expand panel
   *
   * @since 1.0.0
   */
  onItemOpen(item: DocumentInterface): void {
    item.expanded = true;
    this.documentsStructure.forEach((el: any) => {
      if (el.title !== item.title && el.expanded) {
        el.expanded = false;
      }
    });
  }

  /**
   * Handle side menu to close panel
   *
   * @since 1.0.0
   */
  onItemClose(item: DocumentInterface): void {
    item.expanded = false;
  }

  /**
   * Handle click show documents not virtual
   *
   * @since 1.0.0
   */
  onShowDocsNotVirtual(): void {
    this.showDocumentsNotVirtual = true;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click hide documents not virtual
   *
   * @since 1.0.0
   */
  onHideDocsNotVirtual(): void {
    this.showDocumentsNotVirtual = false;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click show documents expired
   *
   * @since 1.0.0
   */
  onShowDocsExpired(): void {
    this.showDocumentsExpired = true;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click hide documents expired
   *
   * @since 1.0.0
   */
  onHideDocsExpired(): void {
    this.showDocumentsExpired = false;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click show documents not inserted
   *
   * @since 1.0.0
   */
  onShowDocsNotInserted(): void {
    this.showDocumentsNotInserted = true;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click hide documents expired
   *
   * @since 1.0.0
   */
  onHideDocsNotInserted(): void {
    this.showDocumentsNotInserted = false;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click show structure deleted
   *
   * @since 1.0.0
   */
  onShowStructureDeleted(): void {
    this.showStructureDeleted = true;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Handle click hide structure deleted
   *
   * @since 1.0.0
   */
  onHideStructureDeleted(): void {
    this.showStructureDeleted = false;

    this.documentArchiveIsLoading = true;
    let filteredData = [];
    if (this.allDocs && Array.isArray(this.allDocs)) {
      filteredData = this.filterDisplayedDocs(this.allDocs);
    }

    this.displayedDocs = filteredData;
    this.countDocPerArea();

    this.documentArchiveIsLoading = false;
  }

  /**
   * Does this node still own at least one document row?
   *
   * The side menu is built on allDocs, the archive on the tree returned by
   * filterDisplayedDocs, which prunes a deleted structure left without rows. Without this
   * check the menu would keep a voice pointing at a section the archive does not render.
   * A node owning no content array IS a document row: areas and sections own one.
   *
   * @since 3.0.0
   */
  hasAnyDocument(node: any): boolean {
    if (!node) {
      return false;
    }
    if (!Array.isArray(node.content)) {
      return true;
    }
    return node.content.some((child: any) => this.hasAnyDocument(child));
  }

  /**
   * A soft deleted document row that the back end has already queued for the hard delete
   * cron. The queue (toHardDelete + hardDeleteDate) is written only by a manual user
   * deletion, never by the cascade that follows the removal of a mapping element, so this
   * is what tells the two kinds of soft deleted rows apart on this page.
   *
   * @since 3.0.0
   */
  isPendingHardDelete(node: any): boolean {
    return (
      node.deleted === true &&
      node.toHardDelete === true &&
      typeof node.hardDeleteDate !== "undefined" &&
      node.hardDeleteDate !== null &&
      node.hardDeleteDate !== ""
    );
  }

  /**
   * Remove from the archive tree, in place, only the documents queued for the hard delete.
   *
   * The tree builder keeps every soft deleted document of a deleted structure
   * (documentTypes.ts, structureDocDeleted flag). Dropping all of them would hide the
   * deleted structures themselves, because the cascade of a removed mapping element soft
   * deletes all their documents: the node would be left empty and pruned by
   * filterDisplayedDocs. Only a manual deletion carries the hard delete queue, so this pass
   * still keeps a document deleted from this page out of the list, and out of the counters,
   * after the reload.
   * Only leaves are dropped: nodes owning a content array are areas or sections.
   *
   * @since 3.0.0
   */
  removeHardDeletedDocs(nodes: any): void {
    if (!Array.isArray(nodes)) {
      return;
    }
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      if (!node) {
        continue;
      }
      if (Array.isArray(node.content)) {
        this.removeHardDeletedDocs(node.content);
      } else if (this.isPendingHardDelete(node)) {
        nodes.splice(i, 1);
      }
    }
  }

  /**
   * Handle docs filtering
   *
   * @since 1.0.0
   */
  filterDisplayedDocs(docs: any) {
    // Shared & Temporary users
    if (this.loggedUserShared || this.loggedUserTemporary) {
      switch (docs.keyTranslateTitle) {
        case "properties.documents.gestionaleAmministrativo":
          if (!this.currentRole.propertiesPermission.gestionaleAmministrativo) {
            return {};
          }
          break;
        case "properties.documents.verbaliconvocazioni":
          if (!this.currentRole.propertiesPermission.gestionaleContabile) {
            return {};
          }
          break;
        case "properties.documents.urbanisticaEdEdilizia":
          if (!this.currentRole.propertiesPermission.urbanisticaEdEdilizia) {
            return {};
          }
          break;
        case "properties.documents.catasto":
          if (!this.currentRole.propertiesPermission.catasto) {
            return {};
          }
          break;
        case "properties.documents.locazioneContrdiLocazione":
          if (!this.currentRole.propertiesPermission.contratti) {
            return {};
          }
          break;
        case "properties.documents.contratti":
          if (!this.currentRole.propertiesPermission.contratti) {
            return {};
          }
          break;
        case "properties.documents.lineaVita":
          if (!this.currentRole.propertiesPermission.lineaVita) {
            return {};
          }
          break;
        case "properties.documents.amiantoDoc":
          if (!this.currentRole.propertiesPermission.amiantoDoc) {
            return {};
          }
          break;
        case "properties.documents.edile":
          if (!this.currentRole.propertiesPermission.edile) {
            return {};
          }
          break;
        case "properties.documents.impiantoElettrico":
          if (!this.currentRole.propertiesPermission.impiantoElettrico) {
            return {};
          }
          break;
        case "properties.documents.riscaldamentoCondizionamento":
          if (!this.currentRole.propertiesPermission.riscaldamentoCondizionamento) {
            return {};
          }
          break;
        case "properties.documents.ascensoriEMontauto":
          if (!this.currentRole.propertiesPermission.ascensori) {
            return {};
          }
          break;
        case "properties.documents.trattamentoAcqua":
          if (!this.currentRole.propertiesPermission.trattamentoAcqua) {
            return {};
          }
          break;
        case "properties.documents.impiantiElettronici":
          if (!this.currentRole.propertiesPermission.impiantiElettronici) {
            return {};
          }
          break;
        case "properties.documents.attivitaSottopostePrevenzioneIncendi":
          if (!this.currentRole.propertiesPermission.antincendio) {
            return {};
          }
          break;
        case "properties.documents.impiantiAntincendio":
          if (!this.currentRole.propertiesPermission.antincendio) {
            return {};
          }
          break;
        case "properties.documents.impiantiSpeciali":
          if (!this.currentRole.propertiesPermission.impiantiSpeciali) {
            return {};
          }
          break;
        case "properties.documents.impiantiRicreativiSportivi":
          if (!this.currentRole.propertiesPermission.impiantiRicreativiSportivi) {
            return {};
          }
          break;
        case "properties.documents.R6manutenzioni":
          if (!this.currentRole.propertiesPermission.preventivi) {
            return {};
          }
          break;
        case "properties.documents.unitaImmobiliari":
          if (!this.currentRole.propertiesPermission.unitaImmobiliari) {
            return {};
          }
          break;
      }
    }
    if (
      typeof docs.active !== "undefined" &&
      !docs.active &&
      !this.showStructureDeleted
    ) {
      return {};
    }
    // Deleted structure left without documents (all of them queued for the hard delete and
    // removed by removeHardDeletedDocs): the empty node would fall through to the leaf branch
    // below and be rendered as a section header with no rows under it.
    if (
      typeof docs.active !== "undefined" &&
      !docs.active &&
      Array.isArray(docs.content) &&
      !docs.content.length
    ) {
      return {};
    }
    if (docs.content && Array.isArray(docs.content) && docs.content.length) {
      let values = {};
      const contents = this.filterDisplayedDocs(docs.content);
      if (contents && Array.isArray(contents) && contents.length) {
        values = {
          active: docs.active,
          content: contents,
          docId: docs.docId,
          structureId: docs.structureId,
          keyTranslateTitle: docs.keyTranslateTitle,
          title: docs.title,
          type: docs.type,
        };
      }
      return values;
    } else {
      if (docs && Array.isArray(docs) && docs.length) {
        const content = [];
        docs.forEach((docs: any) => {
          const ret = this.filterDisplayedDocs(docs);
          if (
            !(Object.keys(ret).length === 0 && ret.constructor === Object) &&
            ret !== ""
          ) {
            content.push(ret);
          }
        });
        return content;
      } else {
        let found = false;

        if (
          this.showDocumentsNotInserted && 
          this.showDocumentsExpired && 
          this.showDocumentsNotVirtual
        ) {
          // show all docs
          found = true;
        } else if (
          !this.showDocumentsNotInserted &&
          this.showDocumentsExpired &&
          this.showDocumentsNotVirtual
        ) {
          // show only inserted docs
          if (docs.data) {
            found = true;
          }
        } else if (
          this.showDocumentsNotInserted &&
          !this.showDocumentsExpired &&
          this.showDocumentsNotVirtual
        ) {
          // show all docs not expired
          if (!docs.expired) {
            found = true;
          }
        } else if (
          !this.showDocumentsNotInserted &&
          !this.showDocumentsExpired &&
          this.showDocumentsNotVirtual
        ) {
          // show only inserted docs and not expired
          if (docs.data && !docs.expired) {
            found = true;
          }
        } else if (!this.showDocumentsNotVirtual) {
          // show only virtual
          if (docs.mockup) {
            found = true;
          }
        }

        // Only Privacy
        if (
          this.subscriptionType === 'PRIVACY' && 
          !this.loggedUserAdmin &&
          !docs.privacy
        ) {
          found = false;
        }

        if (found) {
          return docs;
        }
      }
    }
    return "";
  }

  /**
   * Handle click to show document notes
   *
   * @since 1.0.0
   */
  onViewDocumentNotes(): void {
    const note = [];

    if (this.condoStructure) {
      for (const key in this.condoStructure) {
        if (
          Array.isArray(this.condoStructure[key]) &&
          this.condoStructure[key].length
        ) {

          let structureTranslated = '';
          let find = false;
          const structureTemplate = JSON.parse(JSON.stringify(this.condoStructureTemplate));
          if (structureTemplate && Array.isArray(structureTemplate)) {
            for (const level1 of structureTemplate) {
              if (level1.content && Array.isArray(level1.content)) {
                for (const level2 of level1.content) {
                  // if (level2.title === `properties.structures.${key}`) {
                  if (level2.key === key) {
                    structureTranslated = level2.translation[this.lang];
                    find = true;
                    break;
                  }
                }
              }
              if (find) {
                break;
              }
            }
          }

          this.condoStructure[key].forEach((el: any, index: number) => {
            if (el.active && el.note && Array.isArray(el.note) && el.note.length) {
              el.note.forEach((subEl: any, indexNote: number) => {
                note.push({
                  createdAt: subEl.createdAt,
                  createdBy:
                    subEl.createdBy.firstName + " " + subEl.createdBy.lastName,
                  text: subEl.text,
                  name: el.nome ? el.nome : "",
                  // structure: this.translateService.instant(
                  //   "properties.structures." + key
                  // ),
                  structure: structureTranslated,
                  structureName: key,
                  structurePosition: index,
                  structureId: el._id,
                  notePosition: indexNote,
                  noteId: subEl._id,
                });
              });
            }
          });
        }
      }
    }

    const noteDialogRef = this.dialog.open(
      PropertyDetailDocumentNoteDialogComponent,
      {
        data: {
          modalTitle: this.translateService.instant(
            "properties.documentsPage.noteList"
          ),
          condoStructureId: this.condoStructure._id,
          notes: note,
        },
        minWidth: "50%",
      }
    );
    noteDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res && res.reload) {
          this.getStructureData();
        }
      });
  }

  /**
   * Export properties in a file with given format
   *
   * @since 1.0.0
   */
  onExport(target?: string, propertyId?: string): void {
    // If target is not provided, than just export all properties
    if (!target) {
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        "properties.list.exportFileName"
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getExport().subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    } else if (target && typeof propertyId !== "undefined") {
      // Handle export for missing/inserted documents
      const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
      const fileName = `${this.translateService.instant(
        `properties.list.${target}`
      )} ${date}`;
      this.subscriptions.push(
        this.propertiesService.getDocExport(target, propertyId).subscribe(
          (res) => {
            const fileBlob = res;
            const blob = new Blob([fileBlob], {
              type: "application/vnd.ms-excel",
            });
            FileSaver.saveAs(blob, `${fileName}.xlsx`);
          },
          (err) => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Handle scroll to selected title
   *
   * @since 1.0.0
   */
  onTitleClicked(rawTitle: any) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    setTimeout(() => {
      // const title = this.translateService.instant(rawTitle);

      const docsWindow = document.getElementById("doc-wrapper");
      const target = document.getElementById(rawTitle);

      const top = target.offsetTop;

      docsWindow.scrollTo({
        top,
        behavior: "smooth",
      });
    }, 500);
  }

  /**
   * Send docs by email
   *
   * @since 1.0.0
   */
  onSendByEmail() {
    if (!this.selectedDocs.length) {
      this.toastrService.error(
        this.translateService.instant("common.errors.chooseElementFirst")
      );
      return;
    }
    if (this.selectedDocs.length > this.configService.maximumNumberOfAttachmentsPerEmail) {
      this.toastrService.error(
        this.translateService.instant("common.warnings.maximumAttachmentsReached", { 
          max: this.configService.maximumNumberOfAttachmentsPerEmail
        })
      );
      return;
    }

    // Open dialog
    const newMessageDialogRef = this.newMessageDialog.open(
      SendEmailDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          phonebook: this.company.get("phonebook")
            ? this.company.get("phonebook")
            : [],
          insertedDocs: this.insertedDocs,
          selectedDocs: this.selectedDocs,
          companyData: this.loggedUserMaster ? this.companyData : "",
          propertyName: this.propertyData.name,
          propertyId: this.propertyId,
          sentFromSupplierPage: false,
          // body: "testo predefinito",
        },
        disableClose: true,
      }
    );

    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    newMessageDialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        this.toastrService.success(
          this.translateService.instant("communications.add.sendedSuccessfully")
        );
      });
  }

  /**
   * Update docs array with incoming element
   *
   * @since 1.0.0
   */
  onDocSelected(doc: any, checked: boolean): void {
    if (doc && checked) {
      this.selectedDocs.push(doc.value.docId);

      if (this.selectedDocs.length > this.configService.maximumNumberOfAttachmentsPerEmail) {
        this.toastrService.error(
          this.translateService.instant("common.warnings.maximumAttachmentsReached", { 
            max: this.configService.maximumNumberOfAttachmentsPerEmail
          })
        );
      }
    } else if (doc && !checked) {
      const index = this.selectedDocs.indexOf(doc.value.docId);
      this.selectedDocs.splice(index, 1);
    }
  }

  /**
   * Update docs array with incoming element
   *
   * @since 1.0.0
   */
  onAllDocsSelected(docs: any, event: any): void {
    // event.
    // if (doc && checked) {
    //   this.selectedDocs.push(doc.value.docId);
    // } else if (doc && !checked) {
    //   const index = this.selectedDocs.indexOf(doc.value.docId);
    //   this.selectedDocs.splice(index, 1);
    // }
  }

  /**
   * Auto-select checkboxes of same sub-group
   *
   * @since 1.0.0
   */
  onAllSubDocsSelected(items: any[], checked: boolean) {
    let add = false;
    if (items && items.length) {
      items.forEach((item) => {
        if (item.data && item.data.document) {
          item.checked = checked;
          if (checked) {
            add = true;
            this.selectedDocs.push(item.docId);
          } else if (!checked) {
            const index = this.selectedDocs.indexOf(item.docId);
            this.selectedDocs.splice(index, 1);
          }
        }
      });
      
      if (add && this.selectedDocs.length > this.configService.maximumNumberOfAttachmentsPerEmail) {
        this.toastrService.error(
          this.translateService.instant("common.warnings.maximumAttachmentsReached", { 
            max: this.configService.maximumNumberOfAttachmentsPerEmail
          })
        );
      }
    }
  }

  originalOrder = (
    a: KeyValue<number, string>,
    b: KeyValue<number, string>
  ): number => {
    return 0;
  };

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortData(sort: Sort, i: string, t: string, x: string, pos: string): any {
    let sortedData = [];
    if (pos === "1") {
      sortedData = this.displayedDocs[i].content[t].content[x].content;
    } else if (pos === "2") {
      sortedData = this.displayedDocs[i].content[t].content;
    } else if (pos === "3") {
      sortedData = this.displayedDocs[i].content;
    }

    if (!this.sortActive) {
      this.currentOriginalOrder = JSON.parse(JSON.stringify(sortedData));
    }

    const data = sortedData;
    if (!sort.active || sort.direction === "") {
      sortedData = this.currentOriginalOrder;
      if (pos === "1") {
        this.displayedDocs[i].content[t].content[x].content = sortedData;
      } else if (pos === "2") {
        this.displayedDocs[i].content[t].content = sortedData;
      } else if (pos === "3") {
        this.displayedDocs[i].content = sortedData;
      }
      this.sortActive = false;
      return;
    }

    sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === "asc";
      switch (sort.active) {
        case "title":
          return this.compare(
            a.title.toLowerCase(),
            b.title.toLowerCase(),
            isAsc
          );
        case "data.contenutoDocumento":
          const aContenutoDocumento =
            a.data && a.data.contenutoDocumento
              ? a.data.contenutoDocumento.toLowerCase()
              : "";
          const bContenutoDocumento =
            b.data && b.data.contenutoDocumento
              ? b.data.contenutoDocumento.toLowerCase()
              : "";
          return this.compare(aContenutoDocumento, bContenutoDocumento, isAsc);
        case "data.ragioneSocialeFornitore":
          const aRagioneSocialeFornitore =
            a.data && a.data.ragioneSocialeFornitore
              ? a.data.ragioneSocialeFornitore
              : "";
          const bRagioneSocialeFornitore =
            b.data && b.data.ragioneSocialeFornitore
              ? b.data.ragioneSocialeFornitore
              : "";
          return this.compare(
            aRagioneSocialeFornitore,
            bRagioneSocialeFornitore,
            isAsc
          );
        case "data.dataDocumento":
          const aDataDocumento =
            a.data && a.data.dataDocumento ? a.data.dataDocumento : "";
          const bDataDocumento =
            b.data && b.data.dataDocumento ? b.data.dataDocumento : "";
          return this.compare(aDataDocumento, bDataDocumento, isAsc);
        case "dateExpired":
          const aDateExpired = a.dateExpired ? a.dateExpired : "";
          const bDateExpired = b.dateExpired ? b.dateExpired : "";
          return this.compare(aDateExpired, bDateExpired, isAsc);
        case "data.docCode":
          const aDocCode = a.data && a.data.docCode ? a.data.docCode : "";
          const bDocCode = b.data && b.data.docCode ? b.data.docCode : "";
          return this.compare(aDocCode, bDocCode, isAsc);
        default:
          return 0;
      }
    });

    if (pos === "1") {
      this.displayedDocs[i].content[t].content[x].content = sortedData;
    } else if (pos === "2") {
      this.displayedDocs[i].content[t].content = sortedData;
    } else if (pos === "3") {
      this.displayedDocs[i].content = sortedData;
    }

    this.sortActive = true;
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Open view structure dialog
   *
   * @since 1.0.0
   */
  onViewStructureDialogOpen() {
    const width = window.innerWidth < 1280 ? "90vw" : "80vw";
    // const dialogRef =
    this.dialog.open(ViewStructureDialogComponent, {
      data: {
        modalTitle: this.translateService.instant(
          "properties.addDocumentsPage.viewStructureDialog.title"
        ),
        condoStructure: this.condoStructure,
        property: this.property,
        propertyData: this.propertyData,
      },
      width,
      maxWidth: width,
    });
  }

  /**
   * Handle click on document form to expire contract
   * itemData: data value of the document
   *
   * @since 1.0.0
   */
  onHandleExpiredContract(
    itemData: any
  ): void {
    const dialogRef = this.dialog.open(
      PropertyExpireContractItemDialogComponent,
      {
        data: {
          documentArchiveId: this.documentArchiveId,
          barCode: itemData.barCode ? itemData.barCode : '',
        },
        width: "600px",
      }
    );
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe(
        (res) => {
          if (!res) {
            return;
          } else {
            // recreate view of all docs to show new inserted doc
            this.allDocs = [];
            if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
              this.getDocumentArchiveDataFree();
            } else {
              this.getDocumentArchiveData();
            }
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle click on document form to delete file
   * modalTitle: title of dialogbox from document name
   * documentKey: doc type, es. AA1, AA2, etc.
   * documentId: the ID of document into array of documentArchive section (es. gestAmmTivoDipendenti)
   * subDocumentId: the ID of sub document into array of docs type (es. AA1)
   * itemData: data value of form items
   * action: edito or show doc data
   *
   * @since 1.0.0
   */
  onCancelContractDialogOpen(
    keyTranslateTitle: string,
    modalTitle: string,
    structureId: string,
    documentKey: string,
    documentId: string,
    subDocumentId: string,
    itemData: any
  ): void {
    const contract = {
      ...itemData,
      documentArchiveId: this.documentArchiveId,
      title: modalTitle,
      companyData: this.loggedUserMaster ? this.companyData : "",
      propertyName: this.propertyName,
      propertyId: this.propertyId,
    };

    // Open dialog
    const dialogRef = this.cancelContractDialog.open(
      CancelContractDialogComponent,
      {
        minWidth: "60%",
        maxHeight: "98vh",
        data: {
          contract,
          subject: this.translateService.instant(
            "communications.fields.cancelContractSubject"
          ),
          body: this.translateService.instant(
            "communications.fields.cancelContractImmediateBody",
            { title: modalTitle.trim() }
          ),
        },
        disableClose: true,
      }
    );

    // On dialog closing, let's update model
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe(
        (res) => {
          if (res) {
            const sendData = {
              msg: res,
            };
            this.subscriptions.push(
              this.documentArchivesService
                .cancelContract(this.documentArchiveId, sendData)
                .pipe(take(1))
                .subscribe(
                  (res) => {
                    if (!res) {
                      return;
                    } else {
                      // recreate view of all docs to show new inserted doc
                      this.allDocs = [];
                      if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
                        this.getDocumentArchiveDataFree();
                      } else {
                        this.getDocumentArchiveData();
                      }
                    }
                  },
                  (err) => {
                    this.helperService.handleError(err);
                  }
                )
            );
          } else {
            // console.warn("TASTO CHIUDI")
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      )
    );
  }
  
  /**
   * Word contracts export
   *
   * @since 1.0.0
   */
  onExportContracts(): void {
    const dialogRef = this.dialog.open(ExportContractsDialogComponent, {
      minWidth: "50%",
      maxHeight: "98vh",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        return;
      });
  }
  
  /**
   * RAS docs export in PDF format
   *
   * @since 1.0.0
   */
  onExportRAS(): void {
    const dialogRef = this.dialog.open(ExportRasDocsDialogComponent, {
      width: "700px",
      data: {
        propertyId: this.propertyId,
      },
      disableClose: true,
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        return;
      });
  }

  /**
   * CPI: disable document
   */
  onDisableCPIDocument(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('properties.documentsPage.dialog.disbaleDoc.title'),
        message: this.translateService.instant('properties.documentsPage.dialog.disbaleDoc.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        const sendData = {
          subDocId: element.docId
        };

        this.subscriptions.push(
          this.documentArchivesService
            .disableDocument(this.documentArchiveId, sendData)
            .pipe(take(1))
            .subscribe(
              (res) => {
                if (!res) {
                  return;
                } else {
                  // recreate view of all docs to show new inserted doc
                  this.allDocs = [];
                  if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
                    this.getDocumentArchiveDataFree();
                  } else {
                    this.getDocumentArchiveData();
                  }
                }
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Open the "Gestione Nome Archiviazione Intervento" dialog.
   *
   * It manages the archive-wide list of "interventi di archiviazione"
   * (create / rename / delete). The list is a single flat list shared by the
   * three subareas that group documents by intervention ("Cause legali",
   * "Interventi successivi", "Pratiche edilizie"): there is no notion of an
   * intervention belonging to a single subarea.
   *
   * Reserved to Admin/Operator roles - the menu entry is gated the same way.
   * The guard below only covers other call paths: the real enforcement stays
   * on the back-end.
   *
   * @since 1.0.0
   */
  onManageInterventionArchiving(): void {
    if (
      !this.loggedUserAdmin ||
      !this.loggedUserPermissions.includes("canCreateDocuments")
    ) {
      this.toastrService.error(
        this.translateService.instant("common.errors.not_authorized")
      );
      return;
    }

    if (!this.documentArchiveId) {
      this.toastrService.error(
        this.translateService.instant(
          "properties.documentsPage.interventionArchiving.errors.archiveNotFound"
        )
      );
      return;
    }

    const dialogRef = this.dialog.open(InterventionArchivingDialogComponent, {
      data: {
        documentArchiveId: this.documentArchiveId,
        propertyName: this.property.get("name"),
        interventions: Array.isArray(this.nameInterventionArchiving)
          ? this.nameInterventionArchiving
          : [],
        documentCounts: this.getInterventionDocumentCounts(),
      },
      width: "780px",
      maxHeight: "92vh",
      disableClose: true,
    });

    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) {
            return;
          }
          // Something changed on the back-end: the whole tree must be rebuilt
          // because a rename cascades on every linked document
          this.allDocs = [];
          if (
            this.subscriptionType === "FREE" ||
            this.subscriptionType === "FREEBASIC"
          ) {
            this.getDocumentArchiveDataFree();
          } else {
            this.getDocumentArchiveData();
          }
        })
    );
  }

  /**
   * Derive the document count per intervention name out of the already-built
   * archive tree. Only the three subareas that group documents by
   * intervention are inspected: their children hold the intervention name as
   * "title" and the related documents in "content".
   *
   * The result is a LOWER BOUND: the tree only holds non-deleted documents
   * while the back-end counts every document (deleted ones and mockups
   * included), so a zero count does NOT guarantee the deletion is accepted.
   * The dialog handles the 409 anyway.
   *
   * @since 1.0.0
   */
  getInterventionDocumentCounts(): any {
    const interventionSubAreas = [
      "properties.documents.gestAmmTivoCauseLegali",
      "properties.documents.edileinterventiSuccessivi",
      "properties.documents.DpraticheEdilizie",
    ];
    // Null-prototype map: the keys are free text typed by the users
    const counts = Object.create(null);

    if (!Array.isArray(this.allDocs)) {
      return counts;
    }

    this.allDocs.forEach((area: any) => {
      if (!area || !Array.isArray(area.content)) {
        return;
      }
      area.content.forEach((subArea: any) => {
        if (
          !subArea ||
          typeof subArea.keyTranslateTitle !== "string" ||
          !Array.isArray(subArea.content) ||
          interventionSubAreas.indexOf(subArea.keyTranslateTitle) === -1
        ) {
          return;
        }
        subArea.content.forEach((group: any) => {
          if (!group || typeof group.title !== "string") {
            return;
          }
          const docsAmount = Array.isArray(group.content)
            ? group.content.length
            : 0;
          const current = Object.prototype.hasOwnProperty.call(
            counts,
            group.title
          )
            ? counts[group.title]
            : 0;
          counts[group.title] = current + docsAmount;
        });
      });
    });

    return counts;
  }

  /**
   * Open the "Gestione Nome Conduttore" dialog.
   *
   * Twin of onManageInterventionArchiving(): it manages the archive-wide list
   * of "conduttori" (`conductorName`), shared by the whole archive and not
   * scoped to a subarea.
   *
   * Reserved to Admin/Operator roles - the menu entry is gated the same way.
   * The guard below only covers other call paths: the real enforcement stays
   * on the back-end.
   *
   * @since 1.0.0
   */
  onManageConductors(): void {
    if (
      !this.loggedUserAdmin ||
      !this.loggedUserPermissions.includes("canCreateDocuments")
    ) {
      this.toastrService.error(
        this.translateService.instant("common.errors.not_authorized")
      );
      return;
    }

    if (!this.documentArchiveId) {
      this.toastrService.error(
        this.translateService.instant(
          "properties.documentsPage.conductorManagement.errors.archiveNotFound"
        )
      );
      return;
    }

    const dialogRef = this.dialog.open(ConductorManagementDialogComponent, {
      data: {
        documentArchiveId: this.documentArchiveId,
        propertyName: this.property.get("name"),
        conductors: Array.isArray(this.conductorName)
          ? this.conductorName
          : [],
        documentCounts: this.getConductorDocumentCounts(),
      },
      width: "780px",
      maxHeight: "92vh",
      disableClose: true,
    });

    this.subscriptions.push(
      dialogRef
        .beforeClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) {
            return;
          }
          // Something changed on the back-end: the whole tree must be rebuilt
          // because a rename cascades on every linked document
          this.allDocs = [];
          if (
            this.subscriptionType === "FREE" ||
            this.subscriptionType === "FREEBASIC"
          ) {
            this.getDocumentArchiveDataFree();
          } else {
            this.getDocumentArchiveData();
          }
        })
    );
  }

  /**
   * Derive the document count per conductor out of the already-built archive
   * tree. Only "Contratti di locazione" groups documents by conductor: its
   * children hold the conductor name as "title" and the related documents in
   * "content".
   *
   * The result is a LOWER BOUND: the tree only holds non-deleted documents
   * while the back-end counts every document (deleted, expired and mockups
   * included), so a zero count does NOT guarantee the deletion is accepted.
   * The dialog handles the 409 anyway.
   *
   * @since 1.0.0
   */
  getConductorDocumentCounts(): any {
    const conductorSubArea = "properties.documents.locazioneContrdiLocazione";
    // Null-prototype map: the keys are free text typed by the users
    const counts = Object.create(null);

    if (!Array.isArray(this.allDocs)) {
      return counts;
    }

    this.allDocs.forEach((area: any) => {
      if (!area || !Array.isArray(area.content)) {
        return;
      }
      area.content.forEach((subArea: any) => {
        if (
          !subArea ||
          subArea.keyTranslateTitle !== conductorSubArea ||
          !Array.isArray(subArea.content)
        ) {
          return;
        }
        subArea.content.forEach((group: any) => {
          if (!group || typeof group.title !== "string") {
            return;
          }
          const docsAmount = Array.isArray(group.content)
            ? group.content.length
            : 0;
          const current = Object.prototype.hasOwnProperty.call(
            counts,
            group.title
          )
            ? counts[group.title]
            : 0;
          counts[group.title] = current + docsAmount;
        });
      });
    });

    return counts;
  }
}
